import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useToast } from '../../src/contexts/ToastContext';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import { UsoModuloRow, UsoFuncionalidadeRow, UsoAcaoRow } from '../../types';
import { moduloTemFuncionalidade, funcionalidadeTemAcao } from '../../src/lib/usoRotas';

interface UsoItem {
  chave: string;
  usuarios_pct: number;
  eventos: number;
  tendencia_pct: number;
}

type Nivel =
  | { tipo: 'geral' }
  | { tipo: 'modulo'; modulo: string }
  | { tipo: 'funcionalidade'; modulo: string; funcionalidade: string };

const CORES = ['#5F6C37', '#F6A937', '#7a8a4a', '#8a8a8a', '#a3a3a3', '#bfbfbf'];

// usage_events grava modulo/funcionalidade/acao como slugs técnicos (ver
// src/lib/usoRotas.ts e os pontos de logUsageEvent no Kanban/Wizard) — sem
// isso, a tela mostrava "pos_venda"/"gestao"/"minha_comissao" cru pro admin.
// Nomes seguem exatamente os já usados no menu principal (App.tsx) e em
// Config, pra não inventar rótulo novo. Item futuro sem entrada aqui cai no
// fallback de formatarNome (troca "_"/"-" por espaço e capitaliza).
const NOMES_AMIGAVEIS: Record<string, string> = {
  // módulos
  crm: 'CRM',
  tarefas: 'Tarefas',
  financeiro: 'Financeiro',
  gestao: 'Gestão',
  geradoras: 'Geradoras',
  marketing: 'Marketing',
  academy: 'Academy',
  vick: 'Vick',
  ferramentas: 'Ferramentas',
  config: 'Configurações',
  dashboard: 'Dashboard',
  // funcionalidades (CRM, Tarefas, Financeiro, Gestão, Marketing, Config)
  prospeccao: 'Prospecção Geo',
  rotas: 'Rotas',
  mapa: 'Mapa Inteligente',
  pos_venda: 'Pós-venda',
  analytics: 'Analytics',
  minha_comissao: 'Minha Comissão',
  funil: 'Funil',
  solicitacoes: 'Solicitações',
  ucs: 'UCs / Clientes',
  faturas: 'Faturas e Cobranças',
  parcelamentos: 'Parcelamentos',
  boletos: 'Boletos',
  folha_pagamento: 'Folha de Pagamento',
  lembretes_cobranca: 'Lembretes de Cobrança',
  cancelamentos: 'Cancelamentos',
  auditoria_pagamentos: 'Auditoria de Pagamentos',
  repasse_equatorial: 'Repasse Equatorial (manual)',
  painel_executivo: 'Painel Executivo',
  previsao_recebiveis: 'Previsão de Recebíveis',
  extracao: 'Extração de Faturas',
  extracao_historico: 'Histórico de Extrações',
  rateio_simulador: 'Rateio — Simulador',
  rateio_dashboard: 'Rateio — Dashboard',
  rateio_ancora: 'Rateio — Âncora',
  rateio_conciliacao: 'Rateio — Conciliação',
  rateio_distribuidoras: 'Rateio — Distribuidoras',
  rateio_antigo: 'Rateio (upload legado)',
  lemit: 'Lemit',
  // "inicio" é a grade de cartões de Ferramentas; "dividir_pdf" também é o nome da ação.
  inicio: 'Início',
  dividir_pdf: 'Dividir PDF',
  campanhas: 'Campanhas',
  criativos: 'Criativos',
  overview: 'Visão geral',
  perfil: 'Perfil',
  dados_bancarios: 'Dados Bancários',
  meu_dinheiro: 'Meu Dinheiro',
  notificacoes: 'Notificações',
  usuarios: 'Usuários',
  equipe_parceiros: 'Equipe de Parceiros',
  pipelines: 'Pipelines',
  slas: 'SLAs',
  escalas: 'Escalas',
  avisos: 'Avisos',
  logs: 'Logs',
  apis: 'APIs',
  termo_signatarios: 'Termo — Signatários',
  empresas_parceiras: 'Empresas Parceiras',
  conta_asaas_vigor: 'Conta Asaas Vigor',
  painel: 'Painel',
  analise: 'Análise',
  inadimplencia: 'Inadimplência',
  // ações -- espelham os mesmos pontos que já viram logAction (Logs)
  mover_etapa: 'Mover etapa',
  criar_negociacao: 'Criar negociação',
  exportar_planilha: 'Exportar planilha',
  transferir_cobranca_cliente: 'Transferir cliente de cobrança',
  assumir_cobranca_cliente: 'Assumir cliente de cobrança',
  emitir_boleto: 'Emitir boleto',
  atualizar_fatura: 'Atualizar fatura',
  criar_fatura: 'Criar fatura',
  cancelar_uc: 'Cancelar UC',
  calcular_fatura: 'Calcular fatura',
  reverter_status_fatura: 'Reverter status da fatura',
  excluir_fatura_duplicada: 'Excluir fatura duplicada',
  confirmar_fatura_duplicada: 'Confirmar fatura duplicada',
  criar_parcelamento: 'Criar parcelamento',
  atualizar_uc: 'Atualizar UC',
  criar_uc: 'Criar UC',
  publicar_pdf_boleto: 'Publicar PDF do boleto',
  regerar_pdf_boleto: 'Regerar PDF do boleto',
  enviar_boleto_cliente: 'Enviar boleto ao cliente',
  enviar_cobranca_email: 'Enviar cobrança por e-mail',
  pular_envio_boleto: 'Pular envio de boleto',
  enviar_skip_com: 'Enviar aviso de cobrança pulada',
  cancelar_boleto: 'Cancelar boleto',
  prorrogar_boleto: 'Prorrogar boleto',
  emitir_boleto_avulso: 'Emitir boleto avulso',
  desfazer_auditoria: 'Desfazer auditoria de pagamento',
  quitar_faturas: 'Quitar faturas',
  pular_calculo: 'Pular cálculo',
  criar_fatura_sem_calculo: 'Criar fatura sem cálculo',
  transferir_cobranca: 'Transferir cobrança',
  reenviar_cobranca_email: 'Reenviar cobrança por e-mail',
  confirmar_pagamento_manual: 'Confirmar pagamento manual',
  pagar_equatorial_manual: 'Pagar Equatorial manualmente',
  atualizar_boleto: 'Atualizar boleto',
  criar_boleto: 'Criar boleto',
  auditar_pagamento: 'Auditar pagamento',
  marcar_fatura_teste: 'Marcar/desmarcar fatura de teste',
  excluir_fatura: 'Excluir fatura',
  validar_fatura: 'Validar fatura',
  criar_tarefa: 'Criar tarefa',
  comentar_tarefa: 'Comentar em tarefa',
  mudar_status_tarefa: 'Mudar status da tarefa',
  mudar_tipo_solicitacao: 'Mudar tipo da solicitação',
  mudar_departamento_solicitacao: 'Mudar departamento da solicitação',
  bloquear_tarefa: 'Bloquear tarefa',
  mudar_solicitante: 'Mudar solicitante',
  excluir_tarefa: 'Excluir tarefa',
  criar_subtarefa: 'Criar subtarefa',
  anexar_arquivo_tarefa: 'Anexar arquivo à tarefa',
  mudar_funcao_usuario: 'Mudar função do usuário',
  atualizar_perfil_usuario: 'Atualizar perfil do usuário',
  mudar_departamento_usuario: 'Mudar departamento do usuário',
  excluir_usuario: 'Excluir usuário',
  mudar_status_usuario: 'Mudar status do usuário',
  transferir_pendencias_usuario: 'Transferir pendências de usuário',
  resetar_senha_usuario: 'Resetar senha do usuário',
  limpar_logs_usuario: 'Limpar logs do usuário',
  atualizar_permissoes_usuario: 'Atualizar permissões do usuário',
  criar_usuario: 'Criar usuário',
  // ações -- negociação (CRM > detalhe, negociacao_logs)
  gerenciar_uc_negociacao: 'Gerenciar UC da negociação',
  reenviar_analise_com_avalista: 'Reenviar análise com avalista',
  remover_avalista: 'Remover avalista',
  cadastrar_avalista: 'Cadastrar avalista',
  mudar_pipeline_negociacao: 'Mudar funil da negociação',
  mudar_etapa_detalhe: 'Mudar etapa (detalhe)',
  mudar_origem_negociacao: 'Mudar origem da negociação',
  transferir_negociacao: 'Transferir negociação',
  transferir_negociacoes_em_massa: 'Transferir negociações em massa',
  gerar_termo_adesao: 'Gerar termo de adesão',
  gerar_simulacao: 'Gerar simulação',
  reenviar_analise_cadastral: 'Reenviar para validação de dados',
  marcar_troca_titularidade: 'Marcar troca de titularidade',
  definir_termo_valido: 'Definir termo assinado válido',
  mudar_status_negociacao: 'Mudar status da negociação',
  reativar_negociacao: 'Reativar negociação',
  excluir_uc_negociacao: 'Excluir UC da negociação',
  excluir_documento_negociacao: 'Excluir documento da negociação',
  alterar_plano_negociacao: 'Alterar plano da negociação',
  marcar_negociacao_perdida: 'Marcar negociação como perdida',
  editar_dados_cliente: 'Editar dados do cliente',
  favoritar_negociacao: 'Favoritar negociação',
  mudar_prioridade_negociacao: 'Mudar prioridade da negociação',
  editar_proxima_acao: 'Editar próxima ação',
  avaliar_negociacao: 'Avaliar negociação',
  adicionar_nota_negociacao: 'Adicionar nota',
  editar_nota_negociacao: 'Editar nota',
  excluir_nota_negociacao: 'Excluir nota',
  excluir_negociacao: 'Excluir negociação',
  rejeitar_exclusao_negociacao: 'Rejeitar exclusão de negociação',
  resolver_pendencia_documental: 'Resolver pendência documental',
  // ações -- indicações e parceiros (CRM)
  mudar_status_indicacao: 'Mudar status de indicação',
  mudar_status_parceiro: 'Mudar status de parceiro',
  salvar_meta_comercial: 'Salvar meta comercial',
  desconsiderar_sla: 'Desconsiderar SLA',
  reconsiderar_sla: 'Reconsiderar SLA',
  // ações -- rotas e visitas (CRM)
  criar_rota_inteligente: 'Criar rota inteligente',
  agendar_visitas_selecionadas: 'Agendar visitas selecionadas',
  reagendar_tarefas_selecionadas: 'Reagendar visitas selecionadas',
  reagendar_visita: 'Reagendar visita',
  atribuir_vendedor_selecionadas: 'Atribuir vendedor às selecionadas',
  remover_da_rota_selecionadas: 'Remover selecionadas do roteiro',
  iniciar_rota: 'Iniciar rota',
  checkin_visita_foto: 'Check-in de visita (com foto)',
  checkin_visita_comercial: 'Check-in de visita comercial',
  encerrar_visita: 'Encerrar visita',
  finalizar_rota: 'Finalizar rota',
  cancelar_rota: 'Cancelar rota',
  salvar_observacoes_rota: 'Salvar observações da rota',
  reordenar_parada: 'Reordenar parada',
  mudar_modalidade_visita: 'Mudar modalidade da visita',
  salvar_config_expediente: 'Salvar configuração de expediente',
  encerrar_leitura_medidor: 'Encerrar leitura de medidor',
  exportar_medidores_equatorial: 'Exportar medidores (Equatorial)',
  enriquecer_lead_lemit: 'Enriquecer lead via Lemit',
  importar_leads_prospeccao: 'Importar leads (prospecção)',
  criar_rota_prospeccao: 'Criar rota (prospecção)',
  // ações -- financeiro (documentos, ucs, lembretes, comissões)
  gerar_termo_cancelamento: 'Gerar termo de cancelamento',
  gerar_notificacao_mora: 'Gerar notificação de mora',
  gerar_comunicado_advertencia: 'Gerar comunicado de advertência',
  gerar_aditivo_contratual: 'Gerar aditivo contratual',
  agendar_lembrete: 'Agendar lembrete',
  concluir_lembrete: 'Concluir lembrete',
  descartar_lembrete: 'Descartar lembrete',
  ciente_aviso: 'Ciente no aviso da UC',
  encerrar_aviso: 'Encerrar aviso da UC',
  prazo_aviso: 'Alterar prazo do aviso da UC',
  anexar_documento_uc: 'Anexar documento à UC',
  remover_documento_uc: 'Remover documento da UC',
  remover_link_uc: 'Remover link da UC',
  alternar_configuracao_uc: 'Alternar configuração da UC',
  cancelar_parcelamento: 'Cancelar parcelamento',
  enfileirar_cancelamento_boleto: 'Enfileirar cancelamento de boleto',
  recalcular_comissoes: 'Recalcular comissões',
  aprovar_comissao: 'Aprovar comissão',
  resetar_status_comissao: 'Resetar status de comissão',
  confirmar_pagamento_comissao: 'Confirmar pagamento de comissão',
  criar_cliente_rapido: 'Criar cliente rápido',
  // ações -- config
  atualizar_empresa_parceira: 'Atualizar empresa parceira',
  criar_empresa_parceira: 'Criar empresa parceira',
  desativar_usuario_parceiro: 'Desativar usuário parceiro',
  reativar_usuario_parceiro: 'Reativar usuário parceiro',
  salvar_escala: 'Salvar escala de trabalho',
  cadastrar_ausencia: 'Cadastrar ausência',
  excluir_ausencia: 'Excluir ausência',
  decidir_solicitacao_ausencia: 'Decidir solicitação de ausência',
  publicar_novidades_ti: 'Publicar novidades de TI',
  alternar_inclusao_novidade: 'Alternar inclusão de novidade',
  salvar_aviso_sistema: 'Salvar aviso do sistema',
  publicar_aviso_sistema: 'Publicar aviso do sistema',
  arquivar_aviso_sistema: 'Arquivar aviso do sistema',
  salvar_aviso_consumidor: 'Salvar aviso do consumidor',
  publicar_aviso_consumidor: 'Publicar aviso do consumidor',
  arquivar_aviso_consumidor: 'Arquivar aviso do consumidor',
  salvar_configuracao_api: 'Salvar configuração de API',
  salvar_signatarios_termo: 'Salvar signatários do termo',
  salvar_preferencias_notificacao: 'Salvar preferências de notificação',
  salvar_configuracao_sla: 'Salvar configuração de SLA',
  criar_pipeline: 'Criar funil',
  editar_pipeline: 'Editar funil',
  excluir_pipeline: 'Excluir funil',
  criar_etapa_pipeline: 'Criar etapa de funil',
  editar_etapa_pipeline: 'Editar etapa de funil',
  excluir_etapa_pipeline: 'Excluir etapa de funil',
  reordenar_etapas_pipeline: 'Reordenar etapas de funil',
  // ações -- gestão e análise cadastral
  pedir_reanalise: 'Pedir reanálise',
  marcar_boas_vindas_enviadas: 'Marcar boas-vindas enviadas',
  reabrir_analise_edicao: 'Reabrir análise para edição',
  finalizar_analise_cadastral: 'Finalizar validação de dados',
  confirmar_leitura_notas_analise: 'Confirmar leitura de notas',
  anexar_documento_negociacao: 'Anexar documento à negociação',
  salvar_dados_cliente_analise: 'Salvar dados do cliente (análise)',
  enviar_pendencia_documental: 'Enviar pendência documental',
  salvar_analise_uc: 'Salvar análise da UC',
  liberar_aprovacao_ressalvas: 'Liberar aprovação com ressalvas',
  anexar_relatorio_serasa: 'Anexar relatório Serasa',
  remover_relatorio_serasa: 'Remover relatório Serasa',
  // ações -- rateio
  excluir_rateio: 'Excluir rateio',
  importar_rateio: 'Importar rateio',
  aprovar_cadastro_uc_rateio: 'Aprovar cadastro de UC (rateio)',
  vincular_ucs_ug: 'Vincular UCs à UG',
  desvincular_uc_ug: 'Desvincular UC da UG',
  atualizar_ug: 'Atualizar UG',
  criar_ug: 'Criar UG',
  atualizar_distribuidora: 'Atualizar distribuidora',
  criar_distribuidora: 'Criar distribuidora',
  registrar_geracao_mensal: 'Registrar geração mensal',
  // ações -- perfil próprio, comissão de parceiros, mapa e tarefas (camada api.*)
  atualizar_perfil_proprio: 'Atualizar perfil próprio',
  alterar_senha_propria: 'Alterar senha própria',
  mudar_status_parcela_comissao: 'Mudar status de parcela de comissão',
  decidir_grupo_a_comissao: 'Decidir comissão Grupo A',
  criar_tarifa_energia: 'Criar tarifa de energia',
  salvar_meta_salario_vendedor: 'Salvar meta/salário de vendedor',
  atualizar_coordenadas_mapa: 'Atualizar coordenadas do mapa',
  editar_titulo_tarefa: 'Editar título da tarefa',
  editar_descricao_tarefa: 'Editar descrição da tarefa',
  adicionar_item_checklist: 'Adicionar item ao checklist',
  editar_item_checklist: 'Editar item do checklist',
  marcar_item_checklist: 'Marcar item do checklist',
  excluir_item_checklist: 'Excluir item do checklist',
  transferir_tarefa: 'Transferir tarefa',
  assumir_tarefa: 'Assumir tarefa',
  remover_anexo_tarefa: 'Remover anexo da tarefa',
  reagendar_tarefa: 'Reagendar tarefa',
  // ações -- repasse, rateio novo, avisos e visitas (achadas na varredura de RPCs)
  dar_baixa_repasse_equatorial: 'Dar baixa em repasse Equatorial',
  definir_modalidade_gd: 'Definir modalidade GD',
  registrar_espelho_rateio: 'Registrar espelho de rateio',
  salvar_rateio_simulacao: 'Salvar simulação de rateio',
  confirmar_aviso_sistema: 'Confirmar aviso do sistema',
  confirmar_novidades_ti: 'Confirmar novidades de TI',
  avisar_ausencia: 'Avisar ausência',
  criar_visita: 'Criar visita',
  // ações -- achadas na varredura de edge functions (functions.invoke)
  gerar_troca_titularidade: 'Gerar troca de titularidade',
  sincronizar_termo_autentique: 'Sincronizar termo com Autentique',
  gerar_termo_avalista: 'Gerar termo do avalista',
  geocodificar_leads: 'Geocodificar leads',
  buscar_empresas: 'Buscar empresas (prospecção)',
  salvar_credenciais_asaas: 'Salvar credenciais Asaas',
  rotacionar_token_webhook: 'Rotacionar token de webhook',
  criar_conta_bancaria: 'Criar conta bancária',
  definir_conta_bancaria_principal: 'Definir conta bancária principal',
  excluir_conta_bancaria: 'Excluir conta bancária',
  criar_usuario_empresa_parceira: 'Criar usuário de empresa parceira',
  salvar_credenciais_asaas_parceira: 'Salvar credenciais Asaas (parceira)',
  rotacionar_token_webhook_parceira: 'Rotacionar token de webhook (parceira)',
  registrar_envio_documento: 'Registrar envio de documento',
  enviar_lembrete_assinatura: 'Enviar lembrete de assinatura',
  registrar_nao_envio_documento: 'Registrar não envio de documento',
  cancelar_documento_juridico: 'Cancelar documento jurídico',
  gerar_boleto: 'Gerar boleto',
  decidir_biometria_termo: 'Decidir biometria do termo',
  manter_pendentes_expediente: 'Manter pendentes (fim de expediente)',
  gerar_descricoes_novidades_ia: 'Gerar descrições de novidades (IA)',
  excluir_logs: 'Excluir logs',
  cancelar_job_prospeccao: 'Cancelar job de prospecção',
};

const formatarNome = (chave: string): string => {
  if (NOMES_AMIGAVEIS[chave]) return NOMES_AMIGAVEIS[chave];
  return chave
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(palavra => palavra.charAt(0).toUpperCase() + palavra.slice(1))
    .join(' ');
};

const normalizar = (rows: { usuarios: number; eventos: number; usuarios_pct: number; tendencia_pct: number }[], chaveDe: (r: any) => string): UsoItem[] =>
  rows.map(r => ({ chave: chaveDe(r), usuarios_pct: r.usuarios_pct, eventos: r.eventos, tendencia_pct: r.tendencia_pct }));

const UsoView: React.FC = () => {
  const [nivel, setNivel] = useState<Nivel>({ tipo: 'geral' });
  const [periodo, setPeriodo] = useState<7 | 30 | 90>(30);
  const [itens, setItens] = useState<UsoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const { addToast } = useToast();

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      if (nivel.tipo === 'geral') {
        const { data, error } = await supabase.rpc('get_usage_por_modulo', { dias: periodo });
        if (error) throw error;
        setItens(normalizar((data ?? []) as UsoModuloRow[], (r: UsoModuloRow) => r.modulo));
      } else if (nivel.tipo === 'modulo') {
        const { data, error } = await supabase.rpc('get_usage_por_funcionalidade', { p_modulo: nivel.modulo, dias: periodo });
        if (error) throw error;
        setItens(normalizar((data ?? []) as UsoFuncionalidadeRow[], (r: UsoFuncionalidadeRow) => r.funcionalidade));
      } else {
        const { data, error } = await supabase.rpc('get_usage_por_acao', { p_modulo: nivel.modulo, p_funcionalidade: nivel.funcionalidade, dias: periodo });
        if (error) throw error;
        setItens(normalizar((data ?? []) as UsoAcaoRow[], (r: UsoAcaoRow) => r.acao));
      }
    } catch (err) {
      console.error('Erro ao carregar uso:', err);
      setErro('Não foi possível carregar os dados de uso.');
      addToast('Não foi possível carregar os dados de uso.', 'error');
    } finally {
      setLoading(false);
    }
  }, [nivel, periodo, addToast]);

  useEffect(() => { carregar(); }, [carregar]);

  const titulo =
    nivel.tipo === 'geral' ? 'Visão geral' :
    nivel.tipo === 'modulo' ? formatarNome(nivel.modulo) :
    formatarNome(nivel.funcionalidade);

  const voltar = () => {
    if (nivel.tipo === 'funcionalidade') setNivel({ tipo: 'modulo', modulo: nivel.modulo });
    else if (nivel.tipo === 'modulo') setNivel({ tipo: 'geral' });
  };

  const selecionar = (chave: string) => {
    if (nivel.tipo === 'geral') setNivel({ tipo: 'modulo', modulo: chave });
    else if (nivel.tipo === 'modulo') setNivel({ tipo: 'funcionalidade', modulo: nivel.modulo, funcionalidade: chave });
  };

  // Só convida o clique onde o próximo nível realmente pode ter dado --
  // sem isso, itens como Configurações (sem funcionalidade instrumentada)
  // pareciam clicáveis e levavam a uma tela "sem dados", mesmo com uso
  // real registrado no nível atual.
  const podeAprofundarChave = (chave: string): boolean => {
    if (nivel.tipo === 'geral') return moduloTemFuncionalidade(chave);
    if (nivel.tipo === 'modulo') return funcionalidadeTemAcao(nivel.modulo, chave);
    return false;
  };

  return (
    <div className="space-y-6 animate-fade-in text-left">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          {nivel.tipo !== 'geral' && (
            <button onClick={voltar} className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-primary mb-1">
              <ChevronLeft size={16} /> Voltar para {nivel.tipo === 'funcionalidade' ? formatarNome(nivel.modulo) : 'visão geral'}
            </button>
          )}
          <h1 className="text-xl font-bold font-poppins text-gray-900 dark:text-white">{titulo}</h1>
        </div>
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          {([7, 30, 90] as const).map(p => (
            <button
              key={p}
              onClick={() => setPeriodo(p)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${periodo === p ? 'bg-white dark:bg-gray-700 shadow-sm font-semibold text-primary' : 'text-gray-500 dark:text-gray-400'}`}
            >
              {p}d
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500 -mt-2">
        % sobre o total de usuários ativos do sistema no período — não sobre quem tem acesso a cada módulo. Módulos com acesso mais restrito (ex.: Gestão) tendem a mostrar % menor mesmo com adoção alta entre quem pode entrar.
      </p>

      {loading ? (
        <LoadingSpinner label="Carregando dados de uso..." size="sm" />
      ) : erro ? (
        <div className="flex flex-col items-center justify-center gap-3 py-12">
          <p className="text-red-600 dark:text-red-400 text-sm font-semibold text-center">{erro}</p>
          <button
            onClick={carregar}
            className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      ) : itens.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-400 dark:text-gray-500 font-medium">Sem dados de uso neste período.</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {nivel.tipo === 'geral'
              ? 'Tente um período maior (90d) ou volte mais tarde.'
              : 'Ninguém usou esse item específico nesse período — tente um período maior.'}
          </p>
        </div>
      ) : (
        <NivelUso itens={itens} podeAprofundar={podeAprofundarChave} onSelecionar={selecionar} />
      )}
    </div>
  );
};

const NivelUso: React.FC<{ itens: UsoItem[]; podeAprofundar: (chave: string) => boolean; onSelecionar: (chave: string) => void }> = ({ itens, podeAprofundar, onSelecionar }) => {
  const total = itens.reduce((s, i) => s + i.usuarios_pct, 0) || 1;
  let acumulado = 0;
  const fatias = itens.map((item, i) => {
    const proporcao = item.usuarios_pct / total;
    const fatia = { ...item, offset: acumulado, proporcao, cor: CORES[i % CORES.length] };
    acumulado += proporcao;
    return fatia;
  });
  const raio = 80;
  const circunferencia = 2 * Math.PI * raio;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {itens.map(item => {
          const pode = podeAprofundar(item.chave);
          return (
            <button
              key={item.chave}
              onClick={() => pode && onSelecionar(item.chave)}
              disabled={!pode}
              className={`text-left border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-white dark:bg-gray-800 transition-colors ${pode ? 'hover:border-primary/50 cursor-pointer' : 'cursor-default'}`}
            >
              <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 truncate">{formatarNome(item.chave)}</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{item.usuarios_pct}%</div>
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">dos usuários ativos</div>
              {pode && <div className="text-[11px] text-gray-400 dark:text-gray-600 mt-2">ver por dentro →</div>}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-8 flex-wrap">
        <svg width={200} height={200} viewBox="0 0 220 220">
          <circle cx={110} cy={110} r={raio} fill="none" stroke="currentColor" className="text-gray-100 dark:text-gray-800" strokeWidth={28} />
          {fatias.map(f => {
            // primary (CORES[0]) é um verde-oliva escuro que quase some sobre fundo escuro
            // (mesmo problema documentado no index.html para text-primary) — troca pra
            // primary-light no dark mode. As demais cores da paleta não têm esse problema
            // de contraste, então continuam como hex fixo.
            const ehPrimaria = f.cor === CORES[0];
            const pode = podeAprofundar(f.chave);
            return (
              <circle
                key={f.chave}
                cx={110} cy={110} r={raio} fill="none" strokeWidth={28}
                stroke={ehPrimaria ? undefined : f.cor}
                strokeDasharray={`${f.proporcao * circunferencia} ${circunferencia}`}
                strokeDashoffset={-f.offset * circunferencia}
                transform="rotate(-90 110 110)"
                className={`${ehPrimaria ? 'stroke-primary dark:stroke-primary-light' : ''} ${pode ? 'cursor-pointer' : ''}`}
                onClick={() => pode && onSelecionar(f.chave)}
              >
                <title>{`${formatarNome(f.chave)} — ${f.usuarios_pct}%`}</title>
              </circle>
            );
          })}
        </svg>
        <div className="flex flex-col gap-2 text-sm">
          {fatias.map(f => {
            const ehPrimaria = f.cor === CORES[0];
            const pode = podeAprofundar(f.chave);
            return (
              <div
                key={f.chave}
                role={pode ? 'button' : undefined}
                tabIndex={pode ? 0 : undefined}
                onClick={() => pode && onSelecionar(f.chave)}
                onKeyDown={(e) => {
                  if (pode && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onSelecionar(f.chave);
                  }
                }}
                className={`flex items-center gap-2 ${pode ? 'cursor-pointer rounded focus:outline-none focus:ring-2 focus:ring-primary/50' : ''}`}
              >
                <span
                  className={`w-2.5 h-2.5 rounded-sm shrink-0 ${ehPrimaria ? 'bg-primary dark:bg-primary-light' : ''}`}
                  style={ehPrimaria ? undefined : { background: f.cor }}
                />
                <span className="text-gray-700 dark:text-gray-300">{formatarNome(f.chave)} — {f.usuarios_pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
              <th className="py-2 px-3 font-medium">Item</th>
              <th className="py-2 px-3 font-medium">% usuários</th>
              <th className="py-2 px-3 font-medium">Eventos</th>
              <th className="py-2 px-3 font-medium">Tendência</th>
            </tr>
          </thead>
          <tbody>
            {itens.map(item => {
              const pode = podeAprofundar(item.chave);
              return (
                <tr
                  key={item.chave}
                  onClick={() => pode && onSelecionar(item.chave)}
                  className={`border-b border-gray-100 dark:border-gray-800 ${pode ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50' : ''}`}
                >
                  <td className="py-2 px-3 text-gray-900 dark:text-white">{formatarNome(item.chave)}</td>
                  <td className="py-2 px-3">{item.usuarios_pct}%</td>
                  <td className="py-2 px-3">{item.eventos.toLocaleString('pt-BR')}</td>
                  <td className={`py-2 px-3 ${item.tendencia_pct > 0 ? 'text-emerald-600 dark:text-emerald-400' : item.tendencia_pct < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>
                    {item.tendencia_pct > 0 ? '▲' : item.tendencia_pct < 0 ? '▼' : '—'} {Math.abs(item.tendencia_pct)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsoView;
